/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable eqeqeq */
/* eslint-disable radix */
/* eslint-disable react-hooks/exhaustive-deps */
// Libraries
import React, { useEffect, useMemo, useState } from 'react';

// Type
import { Quote } from '@meterup/connect-api';
// Components
import { FormattedSpeed } from '@meterup/connect-ui';

import { formatBandwidth } from '../../../utils/formatBandwidth';
// Utils
import { pluralize } from '../../../utils/String.utils';
// Components
import QuoteBlock from '../QuoteBlock/QuoteBlock';
import QuotesListHeader from '../QuotesListHeader/QuotesListHeader';
import QuotesListSection from '../QuotesListSection/QuotesListSection';

// TODO: get Interface from @api
interface QuotesListProps {
  quotes: Quote[];
  selectedQuotes: Quote[];
  recommendedQuotes: Quote[];
  shouldGroupByBandwidth: boolean;
  selectedQuoteSids: string[];
  quoteLoadingSid: string;
  onSelectQuote: (sid: string) => void;
  onDeselectQuote: (sid: string) => void;
}

function QuotesList({
  quotes,
  selectedQuotes,
  recommendedQuotes,
  selectedQuoteSids,
  quoteLoadingSid,
  shouldGroupByBandwidth,
  onSelectQuote,
  onDeselectQuote,
}: QuotesListProps) {
  // Preferred provider quoteSids
  const preferredProviderQuoteSids = useMemo(
    () => [...quotes, ...selectedQuotes].filter((n) => n.isPreferred).map((n) => n.sid),
    [quotes],
  );

  // Quotes that aren't selected
  const unselectedQuotes = useMemo(
    () => quotes.filter((n) => !selectedQuotes.includes(n)),
    [quotes, selectedQuotes],
  );

  // Quotes that aren't selected but are recommended
  const unselectedRecommendedQuotes = useMemo(
    () => unselectedQuotes.filter((n) => recommendedQuotes.includes(n)),
    [quotes, unselectedQuotes, recommendedQuotes],
  );

  // Preferred provider quotes (not selected or recommended)
  const preferredProviderQuotes = useMemo(
    () =>
      unselectedQuotes.filter(
        (n) =>
          !unselectedRecommendedQuotes.includes(n) && preferredProviderQuoteSids.includes(n.sid),
      ),
    [quotes, unselectedRecommendedQuotes],
  );

  // Find all other quotes (not selected or recommented)
  const allOtherQuotes = useMemo(
    () =>
      quotes.filter(
        (n) =>
          !selectedQuotes.includes(n) &&
          !recommendedQuotes.includes(n) &&
          !preferredProviderQuotes.includes(n),
      ),
    [quotes, selectedQuotes, recommendedQuotes, preferredProviderQuotes],
  );

  // Group all other quotes by download speed
  const allOtherQuotesByDownloadMbps = useMemo(
    () =>
      allOtherQuotes.reduce(
        (acc, quote) => {
          const downloadMbps = formatBandwidth(quote.downloadKbps);
          const existingQuotes = acc[downloadMbps] || [];
          return {
            ...acc,
            [downloadMbps]: [...existingQuotes, quote],
          };
        },
        {} as Record<string, Quote[]>,
      ),
    [quotes, selectedQuotes, recommendedQuotes],
  );

  // Util: get an array of all download speeds
  const allOtherQuotesDownloadMbps = useMemo(
    () => Object.keys(allOtherQuotesByDownloadMbps).sort((a, b) => parseInt(a) - parseInt(b)),
    [quotes, selectedQuotes, recommendedQuotes],
  );

  // State: which section is open
  const [visibleSelectedQuotes, setVisibleSelectedQuotes] = useState(selectedQuotes.length != 0);
  const [visibleRecommendedQuotes, setVisibleRecommendedQuotes] = useState(
    unselectedRecommendedQuotes.length != 0,
  );
  const [visiblePreferredProviderQuotes, setVisiblePreferredProviderQuotes] = useState(
    preferredProviderQuotes.length != 0,
  );
  const [visibleDownloadMbps, setVisibleDownloadMbps] = useState(allOtherQuotesDownloadMbps);
  const [visibleAllOtherQuotes, setVisibleAllOtherQuotes] = useState(allOtherQuotes.length != 0);

  const onDownloadMbpsClick = (downloadMbps: number) => {
    if (visibleDownloadMbps.includes(downloadMbps.toString())) {
      setVisibleDownloadMbps(visibleDownloadMbps.filter((d) => d !== downloadMbps.toString()));
    } else {
      setVisibleDownloadMbps([...visibleDownloadMbps, downloadMbps.toString()]);
    }
  };

  useEffect(() => {
    setVisibleSelectedQuotes(selectedQuotes.length != 0);
  }, [selectedQuotes]);

  useEffect(() => {
    setVisibleRecommendedQuotes(unselectedRecommendedQuotes.length != 0);
  }, [unselectedRecommendedQuotes]);

  useEffect(() => {
    setVisiblePreferredProviderQuotes(preferredProviderQuotes.length != 0);
  }, [preferredProviderQuotes]);

  useEffect(() => {
    setVisibleDownloadMbps(allOtherQuotesDownloadMbps);
  }, [quotes]);

  useEffect(() => {
    setVisibleAllOtherQuotes(allOtherQuotes.length != 0);
  }, [allOtherQuotes]);

  const bandwidthSectionHeader = (downloadMbps: number) => (
    <QuotesListHeader
      onClick={() => onDownloadMbpsClick(downloadMbps)}
      open={visibleDownloadMbps.includes(downloadMbps.toString())}
      title={<FormattedSpeed mbps={downloadMbps} />}
    />
  );

  const selectedQuotesSectionHeader = () => (
    <QuotesListHeader
      onClick={() => setVisibleSelectedQuotes(!visibleSelectedQuotes)}
      open={visibleSelectedQuotes}
      title={pluralize(selectedQuotes.length, 'Selected Quote')}
    />
  );

  const recommendedQuotesSectionHeader = () => (
    <QuotesListHeader
      onClick={() => setVisibleRecommendedQuotes(!visibleRecommendedQuotes)}
      open={visibleRecommendedQuotes}
      title={pluralize(unselectedRecommendedQuotes.length, 'Recommended Quote')}
    />
  );

  const preferredProviderQuotesSectionHeader = () => (
    <QuotesListHeader
      onClick={() => setVisiblePreferredProviderQuotes(!visiblePreferredProviderQuotes)}
      open={visiblePreferredProviderQuotes}
      title={pluralize(preferredProviderQuotes.length, 'Preferred Provider Quote')}
    />
  );

  const allOtherQuotesSectionHeader = () => (
    <QuotesListHeader
      onClick={() => setVisibleAllOtherQuotes(!visibleAllOtherQuotes)}
      open={visibleAllOtherQuotes}
      title={pluralize(allOtherQuotes.length, 'Quote')}
    />
  );

  const renderQuotes = (quotes: Quote[]) => (
    <div className="mb-12 mt-2 border-t border-gray-100">
      {quotes.map((quote: Quote) => (
        <li key={quote.sid} className="mb-1 overflow-hidden ">
          {renderQuote(quote)}
        </li>
      ))}
    </div>
  );

  const renderQuote = (quote: Quote) => (
    <QuoteBlock
      quote={quote}
      selected={selectedQuoteSids.includes(quote.sid)}
      selectionPriority={selectedQuoteSids.findIndex((q) => q === quote.sid) + 1}
      nextSelectionPriority={
        selectedQuoteSids.length < 3 ? selectedQuoteSids.length + 1 : undefined
      }
      preferred={preferredProviderQuoteSids.includes(quote.sid)}
      disabledSelect={selectedQuoteSids.length >= 3}
      loading={quoteLoadingSid === quote.sid}
      onClick={() =>
        selectedQuoteSids.includes(quote.sid)
          ? onDeselectQuote(quote.sid)
          : onSelectQuote(quote.sid)
      }
    />
  );

  // In order we show:
  // 1) Selected Quotes
  // 2) Recommended Quotes (except selected ones)
  // 3) Preferred Quotes
  // 3) All other quotes, sorted by bandwidth
  return (
    <ol>
      {selectedQuotes.length != 0 && (
        <li className="mb-6">
          {selectedQuotesSectionHeader()}
          <QuotesListSection open={visibleSelectedQuotes}>
            {renderQuotes(selectedQuotes)}
          </QuotesListSection>
        </li>
      )}

      {unselectedRecommendedQuotes.length != 0 && (
        <li className="mb-6">
          {recommendedQuotesSectionHeader()}
          <QuotesListSection open={visibleRecommendedQuotes}>
            {renderQuotes(unselectedRecommendedQuotes)}
          </QuotesListSection>
        </li>
      )}

      {preferredProviderQuotes.length != 0 && (
        <li className="mb-6">
          {preferredProviderQuotesSectionHeader()}
          <QuotesListSection open={visiblePreferredProviderQuotes}>
            {renderQuotes(preferredProviderQuotes)}
          </QuotesListSection>
        </li>
      )}

      {shouldGroupByBandwidth ? (
        allOtherQuotesDownloadMbps.map((downloadMbps) => {
          const open = visibleDownloadMbps.includes(downloadMbps.toString());
          return (
            <li key={downloadMbps} className="mb-6">
              {bandwidthSectionHeader(parseInt(downloadMbps))}
              <QuotesListSection open={open}>
                {renderQuotes(allOtherQuotesByDownloadMbps[downloadMbps])}
              </QuotesListSection>
            </li>
          );
        })
      ) : (
        <li className="mb-6">
          {allOtherQuotesSectionHeader()}
          <QuotesListSection open={visibleAllOtherQuotes}>
            {renderQuotes(allOtherQuotes)}
          </QuotesListSection>
        </li>
      )}
    </ol>
  );
}

export default QuotesList;
